/**
 * The contact's own fields, including the custom ones an administrator defined
 * in Step 2 — this is where those definitions finally become a form.
 *
 * Custom fields arrive from the BFF already merged with their values
 * (`Contact.customFields`), so this renders a list rather than joining two.
 * Two rules show up visibly:
 *
 *  - a field whose definition was **deactivated** but which still holds a value
 *    renders read-only rather than vanishing — the value is real, and hiding it
 *    would be a silent data loss to anyone reading the profile;
 *  - the values are submitted as one JSON object, and the server validates each
 *    against its definition, so a wrong-typed value comes back as a field-level
 *    error rather than a form-wide one.
 */
import { useMutation, useQuery } from '@apollo/client';
import { Button, Card, Field } from '@meshhub/design-tokens/ui';
import { Pencil } from 'lucide-react';
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  COLLABORATION_STATUS_LABELS,
  CONTACT_KIND_LABELS,
  GENDER_LABELS,
  SUBSCRIPTION_TYPE_LABELS,
  formatDate,
  fromPersianDigits,
  isoToJalaliDate,
} from '../../lib/format.js';
import { ADMIN_LOOKUPS_QUERY, UPDATE_CONTACT } from '../../lib/queries.js';
import {
  ContactCrmFields,
  centerNameError,
  crmFieldsInput,
  crmFieldsOf,
} from './ContactCrmFields.web.jsx';
import { S } from '../../lib/strings.js';
import {
  CheckboxField,
  FormDialog,
  JalaliDateField,
  MutationError,
  SelectField,
  TextArea,
  TextField,
} from '../FormKit.web.jsx';

const GENDERS = ['MALE', 'FEMALE', 'OTHER'];
const SUBSCRIPTION_TYPES = ['WHITELABEL', 'STANDARD', 'BARTER'];

/** A stored custom-field value (JSON-encoded) as text for display. */
function displayValue(field) {
  if (field.value === null) return S.app.none;
  const parsed = JSON.parse(field.value);
  if (field.type === 'BOOLEAN') return parsed ? S.app.yes : S.app.no;
  if (field.type === 'DATE') return formatDate(parsed);
  if (Array.isArray(parsed)) return parsed.join('، ');
  return String(parsed);
}

export function DetailsPanel({ contact, onSaved, readOnly }) {
  const [editing, setEditing] = useState(false);
  const isPerson = contact.kind === 'PERSON';

  return (
    <Card>
      <div className="mb-4 flex items-center gap-3">
        <h2 className="text-md font-bold">{S.contacts.detail.profile}</h2>
        {!readOnly && (
          <Button variant="ghost" className="ms-auto" onClick={() => setEditing(true)}>
            <Pencil size={14} aria-hidden="true" />
            {S.contacts.detail.editProfile}
          </Button>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4">
        <Field label={S.contacts.detail.kind}>{CONTACT_KIND_LABELS[contact.kind]}</Field>
        <Field label={S.contacts.detail.fieldOfWork}>
          {contact.fieldOfWork?.labelFa ?? S.app.none}
        </Field>
        {isPerson && (
          <>
            <Field label={S.contacts.detail.specialty}>
              {contact.specialty?.labelFa ?? S.app.none}
            </Field>
            <Field label={S.contacts.detail.subSpecialty}>
              {contact.subSpecialty?.labelFa ?? S.app.none}
            </Field>
            <Field label={S.contacts.detail.gender}>
              {contact.gender ? GENDER_LABELS[contact.gender] : S.app.none}
            </Field>
            <Field label={S.contacts.detail.birthDate}>
              {contact.birthDate ? formatDate(contact.birthDate) : S.app.none}
            </Field>
          </>
        )}
        {/* The subscription pipeline, named explicitly now that four
            similarly-shaped status dimensions sit beside it. Same field, same
            history, a label that says which question it answers. */}
        <Field label={S.contacts.detail.collaborationStatus}>
          {COLLABORATION_STATUS_LABELS[contact.collaborationStatus]}
        </Field>
        <Field label={S.contacts.detail.subscriptionType}>
          {contact.subscriptionType
            ? SUBSCRIPTION_TYPE_LABELS[contact.subscriptionType]
            : S.app.none}
        </Field>
        <Field label={S.contacts.detail.city}>{contact.city?.labelFa ?? S.app.none}</Field>
        <Field label={S.contacts.detail.professionalCode}>
          {contact.professionalCode ? (
            <span dir="ltr">{contact.professionalCode}</span>
          ) : (
            S.app.none
          )}
        </Field>
        <Field label={S.contacts.detail.lifecycleStage}>
          {contact.lifecycleStage?.labelFa ?? S.app.none}
        </Field>
        <Field label={S.contacts.detail.salesStage}>
          {contact.salesStage?.labelFa ?? S.app.none}
        </Field>
        <Field label={S.contacts.detail.customerHealth}>
          {contact.customerHealth?.labelFa ?? S.app.none}
        </Field>
        <Field label={S.contacts.detail.engagementStatus}>
          {contact.engagementStatus?.labelFa ?? S.app.none}
        </Field>
        <Field label={S.contacts.detail.isGold}>
          {contact.isGold ? S.app.yes : S.app.no}
        </Field>
        <Field label={S.contacts.detail.facilityType}>
          {contact.facilityType?.labelFa ?? S.app.none}
        </Field>
        <Field label={S.contacts.detail.centerName}>
          {contact.centerName || S.app.none}
        </Field>
        <Field label={S.contacts.detail.acquisitionSource}>
          {contact.acquisitionSource?.labelFa ?? S.app.none}
        </Field>
        <Field label={S.contacts.detail.referrer}>
          {contact.referrerContactId ? (
            // A link, because "who introduced them" is a question that
            // usually leads straight to the next profile.
            <Link
              to={`/contacts/${contact.referrerContactId}`}
              className="text-primary hover:underline"
            >
              {contact.referrerName || contact.referrerContactId}
            </Link>
          ) : (
            S.app.none
          )}
        </Field>
        <Field label={S.contacts.detail.instagram}>
          {contact.instagramId ? <span dir="ltr">@{contact.instagramId}</span> : S.app.none}
        </Field>
        <Field label={S.contacts.detail.address} className="col-span-2">
          {contact.address || S.app.none}
        </Field>
        <Field label={S.contacts.detail.description} className="col-span-2">
          <span className="whitespace-pre-line">{contact.description || S.app.none}</span>
        </Field>
      </div>

      {contact.customFields.length > 0 && (
        <section className="mt-6 border-t border-border pt-5">
          <h3 className="mb-4 text-sm font-medium text-text-muted">
            {S.contacts.detail.customFields}
          </h3>
          <div className="grid grid-cols-2 gap-4">
            {contact.customFields.map((field) => (
              <Field
                key={field.id}
                label={
                  field.isActive ? (
                    field.labelFa
                  ) : (
                    // Deactivated definition, value retained: labelled so the
                    // reader knows why it is not on the edit form.
                    <span className="flex items-center gap-2">
                      {field.labelFa}
                      <span className="text-xs">({S.app.inactive})</span>
                    </span>
                  )
                }
              >
                {displayValue(field)}
              </Field>
            ))}
          </div>
        </section>
      )}

      {editing && (
        <EditDialog
          contact={contact}
          open={editing}
          onOpenChange={setEditing}
          onSaved={onSaved}
        />
      )}
    </Card>
  );
}

function EditDialog({ contact, open, onOpenChange, onSaved }) {
  const isPerson = contact.kind === 'PERSON';
  const [form, setForm] = useState(() => ({
    firstName: contact.firstName ?? '',
    lastName: contact.lastName ?? '',
    orgName: contact.orgName ?? '',
    birthDate: contact.birthDate ?? null,
    gender: contact.gender ?? '',
    fieldOfWorkId: contact.fieldOfWork?.id ?? '',
    specialtyId: contact.specialty?.id ?? '',
    subSpecialtyId: contact.subSpecialty?.id ?? '',
    subscriptionType: contact.subscriptionType ?? '',
    instagramId: contact.instagramId ?? '',
    address: contact.address ?? '',
    description: contact.description ?? '',
  }));
  // The CRM profile fields, kept in their own object so the shared component
  // and its payload builder own them end to end — the create form uses the
  // same two, which is what stops the two forms from drifting.
  const [crm, setCrm] = useState(() => crmFieldsOf(contact));
  // Only ACTIVE definitions are editable; an inactive one is displayed on the
  // panel but has no input here, which is exactly "readable but not writable".
  const [custom, setCustom] = useState(() =>
    Object.fromEntries(
      contact.customFields
        .filter((f) => f.isActive)
        .map((f) => [f.name, f.value === null ? emptyFor(f) : JSON.parse(f.value)]),
    ),
  );
  const [error, setError] = useState(null);

  const { data: fieldsOfWork } = useQuery(ADMIN_LOOKUPS_QUERY, {
    variables: { kind: 'FIELD_OF_WORK', includeInactive: false },
  });
  const { data: specialties } = useQuery(ADMIN_LOOKUPS_QUERY, {
    variables: { kind: 'SPECIALTY', includeInactive: false },
  });
  // Hoisted rather than read inside ContactCrmFields, because submit has to
  // check the center-name rule before it sends. Apollo dedupes it with the
  // component's own query, so this is one request, not two.
  const { data: facilityTypeData } = useQuery(ADMIN_LOOKUPS_QUERY, {
    variables: { kind: 'FACILITY_TYPE', includeInactive: false },
  });
  const facilityTypes = facilityTypeData?.lookups ?? [];
  const [updateContact, { loading }] = useMutation(UPDATE_CONTACT);

  const set = (patch) => setForm((current) => ({ ...current, ...patch }));
  const setField = (name, value) => setCustom((current) => ({ ...current, [name]: value }));

  const allSpecialties = specialties?.lookups ?? [];
  const roots = allSpecialties.filter((s) => !s.parentId);
  const children = allSpecialties.filter((s) => s.parentId === form.specialtyId);

  async function submit() {
    setError(null);
    // Refused HERE as well as on the server. The server's answer is the one
    // that counts; this one arrives without a round trip and points at the
    // field rather than at the form.
    const centerError = centerNameError(crm, facilityTypes);
    if (centerError) {
      setError(new Error(centerError));
      return;
    }
    try {
      await updateContact({
        variables: {
          id: contact.id,
          input: {
            ...(isPerson
              ? {
                  firstName: form.firstName.trim() || null,
                  lastName: form.lastName.trim(),
                  birthDate: form.birthDate,
                  gender: form.gender || null,
                  specialtyId: form.specialtyId || null,
                  subSpecialtyId: form.subSpecialtyId || null,
                }
              : { orgName: form.orgName.trim() }),
            fieldOfWorkId: form.fieldOfWorkId || null,
            subscriptionType: form.subscriptionType || null,
            instagramId: form.instagramId.trim() || null,
            address: form.address,
            description: form.description,
            extraAttributes: JSON.stringify(custom),
            ...crmFieldsInput(crm),
          },
        },
      });
      await onSaved?.();
      onOpenChange(false);
    } catch (err) {
      setError(err);
    }
  }

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title={S.contacts.detail.editProfile}
      onSubmit={submit}
      busy={loading}
      wide
    >
      <MutationError error={error} />

      <div className="grid grid-cols-2 gap-4">
        {isPerson ? (
          <>
            <TextField
              label={S.contacts.detail.lastName}
              value={form.lastName}
              onChange={(e) => set({ lastName: e.target.value })}
              required
            />
            <TextField
              label={S.contacts.detail.firstName}
              value={form.firstName}
              onChange={(e) => set({ firstName: e.target.value })}
            />
            <SelectField
              label={S.contacts.detail.gender}
              value={form.gender}
              onChange={(e) => set({ gender: e.target.value })}
            >
              <option value="">{S.app.none}</option>
              {GENDERS.map((g) => (
                <option key={g} value={g}>
                  {GENDER_LABELS[g]}
                </option>
              ))}
            </SelectField>
            <JalaliDateField
              label={S.contacts.detail.birthDate}
              value={form.birthDate}
              onChange={(iso) => set({ birthDate: iso })}
            />
          </>
        ) : (
          <TextField
            label={S.contacts.detail.orgName}
            className="col-span-2"
            value={form.orgName}
            onChange={(e) => set({ orgName: e.target.value })}
            required
          />
        )}

        <SelectField
          label={S.contacts.detail.fieldOfWork}
          value={form.fieldOfWorkId}
          onChange={(e) => set({ fieldOfWorkId: e.target.value })}
        >
          <option value="">{S.app.none}</option>
          {(fieldsOfWork?.lookups ?? []).map((f) => (
            <option key={f.id} value={f.id}>
              {f.labelFa}
            </option>
          ))}
        </SelectField>

        <SelectField
          label={S.contacts.detail.subscriptionType}
          value={form.subscriptionType}
          onChange={(e) => set({ subscriptionType: e.target.value })}
        >
          <option value="">{S.app.none}</option>
          {SUBSCRIPTION_TYPES.map((t) => (
            <option key={t} value={t}>
              {SUBSCRIPTION_TYPE_LABELS[t]}
            </option>
          ))}
        </SelectField>

        {isPerson && (
          <>
            <SelectField
              label={S.contacts.detail.specialty}
              value={form.specialtyId}
              onChange={(e) => set({ specialtyId: e.target.value, subSpecialtyId: '' })}
            >
              <option value="">{S.app.none}</option>
              {roots.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.labelFa}
                </option>
              ))}
            </SelectField>
            <SelectField
              label={S.contacts.detail.subSpecialty}
              value={form.subSpecialtyId}
              onChange={(e) => set({ subSpecialtyId: e.target.value })}
              // Sub-specialties are the children of the chosen specialty, so
              // the tree the admin built drives the picker directly.
              disabled={children.length === 0}
            >
              <option value="">{S.app.none}</option>
              {children.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.labelFa}
                </option>
              ))}
            </SelectField>
          </>
        )}

        <TextField
          label={S.contacts.detail.instagram}
          value={form.instagramId}
          onChange={(e) => set({ instagramId: e.target.value })}
          dir="ltr"
        />
        <TextField
          label={S.contacts.detail.address}
          value={form.address}
          onChange={(e) => set({ address: e.target.value })}
        />
      </div>

      <TextArea
        label={S.contacts.detail.description}
        rows={3}
        value={form.description}
        onChange={(e) => set({ description: e.target.value })}
      />

      {/* The CRM profile fields, grouped exactly as they are on the create
          form — one component, so the two cannot disagree about what they
          collect or how they send it. */}
      <ContactCrmFields
        values={crm}
        onChange={(patch) => setCrm((current) => ({ ...current, ...patch }))}
        contactId={contact.id}
        referrerName={contact.referrerName}
        facilityTypes={facilityTypes}
      />

      {contact.customFields.some((f) => f.isActive) && (
        <section className="border-t border-border pt-4">
          <h3 className="mb-3 text-sm font-medium text-text-muted">
            {S.contacts.detail.customFields}
          </h3>
          <div className="grid grid-cols-2 gap-4">
            {contact.customFields
              .filter((f) => f.isActive)
              .map((field) => (
                <CustomFieldInput
                  key={field.id}
                  field={field}
                  value={custom[field.name]}
                  onChange={(value) => setField(field.name, value)}
                />
              ))}
          </div>
        </section>
      )}
    </FormDialog>
  );
}

/** The empty value a field type starts from, so the JSON payload stays typed. */
function emptyFor(field) {
  if (field.type === 'MULTISELECT') return [];
  if (field.type === 'BOOLEAN') return false;
  return '';
}

/** One input per custom-field type. The server validates again regardless. */
function CustomFieldInput({ field, value, onChange }) {
  switch (field.type) {
    case 'NUMBER':
      return (
        <TextField
          label={field.labelFa}
          value={value ?? ''}
          inputMode="numeric"
          // Persian digits are converted here so the payload is a real number;
          // the server would reject «۱۲» as text otherwise.
          onChange={(e) => {
            const text = fromPersianDigits(e.target.value).trim();
            onChange(text === '' ? '' : Number.isNaN(Number(text)) ? text : Number(text));
          }}
        />
      );
    case 'DATE':
      return (
        <JalaliDateField
          label={field.labelFa}
          value={value || null}
          onChange={(iso) => onChange(iso ?? '')}
          hint={value ? isoToJalaliDate(value) : undefined}
        />
      );
    case 'BOOLEAN':
      return (
        <div className="flex items-end">
          <CheckboxField
            label={field.labelFa}
            checked={Boolean(value)}
            onChange={(e) => onChange(e.target.checked)}
          />
        </div>
      );
    case 'SELECT':
      return (
        <SelectField
          label={field.labelFa}
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
        >
          <option value="">{S.app.none}</option>
          {field.options.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </SelectField>
      );
    case 'MULTISELECT': {
      const chosen = Array.isArray(value) ? value : [];
      return (
        <fieldset className="flex flex-col gap-2">
          <legend className="text-sm text-text-muted">{field.labelFa}</legend>
          {field.options.map((option) => (
            <CheckboxField
              key={option}
              label={option}
              checked={chosen.includes(option)}
              onChange={() =>
                onChange(
                  chosen.includes(option)
                    ? chosen.filter((v) => v !== option)
                    : [...chosen, option],
                )
              }
            />
          ))}
        </fieldset>
      );
    }
    default:
      return (
        <TextField
          label={field.labelFa}
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
        />
      );
  }
}
